import { useCallback, useEffect } from 'react';
import Head from 'next/head';
import type { GetServerSideProps } from 'next';
import { ProfileLanding, ProfileUnavailable } from '../../../components/symphony/profile-links/ProfileLanding';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { getBootstrapContext } from '../../../lib/tenant-aspects';
import { getPublicProfileCard } from '../../../lib/profile-link-card-server';
import type { PublicProfileCard } from '../../../lib/profile-link-card';
import {
  effectiveCard,
  parseProfileRoute,
  PROFILE_OG_H,
  PROFILE_OG_W,
  profileDeviceFor,
  profileOgMeta,
  type ProfileCta,
  type ProfileDevice,
} from '../../../lib/profile-link-view';
import { loadReferral, routeReferralToStore, saveReferral } from '../../../lib/referral-capture';
import { admitProfileRequest, recordProfileMiss } from '../../../lib/profile-link-rate-limit';
import { parseProfileView, parseSlideVariant } from '../../../lib/profile-link-assets';
import { claimProfileView, PROFILE_CTA_TAP, sendProfileTap, sendProfileView } from '../../../lib/profile-link-view-beacon';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { PROFILE_LINK_PAGE } from '../../../components/symphony/profile-links/constants';
import { isBotUserAgent } from '../../../lib/share-card-view';

/**
 * `/me/<handle>` and `/me/<handle>/<context>` — a Symphony user's PUBLIC profile link
 * (docs/symphony-profile-links-design.md §5). Its reader is anyone who scanned a QR or tapped a link and
 * whose phone did not hand it to the app: no account, no session, often no app.
 *
 * - **Server-rendered**, so an unfurler, a reader with JS off and a screen reader all get the real page.
 * - **One gate, and it is not about the visitor.** The card read answers null unless the OWNER's link is
 *   active and the owner is in `symphonyProfileLinks` (enforced by `ProfileLinkService.GetPublicCard`),
 *   so closing the spec closes every page at once. The spec is never conducted on the visitor: that would
 *   404 every anonymous visitor, which is this page's whole population.
 * - **One neutral page** for every card the adapter cannot serve (§4.5), and for a visitor past the
 *   per-visitor rate limit (`lib/profile-link-rate-limit.ts`, §12.2) — same page, same status, so the
 *   limit never tells an enumerator which answers were real.
 * - **Not frameable** (`frame-ancestors 'none'`) — except as `?view=embed`, the card section an owner puts on
 *   their own website: `frame-ancestors *`, no referral capture (a third-party frame's storage is not the
 *   visitor's), every button in a new tab. Nothing in it acts on a session, so framing it grants nothing.
 * - **`?view=qr`** answers the link's QR code alone as a square PNG, for a deck Wixel designs itself (the words go
 *   to Wixel as text). Same card read and rate limit as the slide.
 * - **`?view=slide`** answers the link's closing slide as a PNG (`lib/profile-slide.ts`), drawn from the same
 *   public card and behind the same rate limit, so it shows exactly what a visitor could read here anyway.
 *   Both are query flags, not path segments, because the session-free exemption (`lib/public-paths.js`,
 *   meta-site #15278) admits only `/me/<handle>[/<context>]` — see `lib/profile-link-assets.ts`.
 * - **`?view=og`** answers the link's share-preview image (the slide fitted to 1200×630), which og:image names, so
 *   every card unfurls as ITSELF rather than the generic Symphony card.
 * - **`noindex` on context links**, and on a card whose name or headline carries markup or a URL; the main
 *   profile is otherwise indexable (goal 4).
 * - **A tap is counted too**: each CTA posts `kind` (app_tap / whatsapp_tap / message_tap) to the same route
 *   before the browser leaves, and fires clickCta. Never from the embed or the owner screens' previews.
 * - **A view is counted by the browser, not the render**: once hydrated, a non-bot reader posts one beacon per
 *   link per browser session (`lib/profile-link-view-beacon.ts` → `/api/profile-link-view`), because the card
 *   read here is cached and is also what every unfurler and prefetch triggers. Never from the embed. In the
 *   apps the same count rides `open-profile-thread` (ai-scheduling-assistant-server `profileLinks.ts`).
 *
 * The exemption that lets this render with no session is `/me/` in `lib/public-paths.js` (server) and
 * `lib/public-routes.ts` (client); `SymphonyApp` renders it chrome-free.
 */

/**
 * `countView`: the server's own user-agent check says this reader is not an unfurler or a scanner, so the page
 * may post its view beacon once it hydrates. Never set on the embed — the card inside the owner's own website
 * is that site's traffic, not a visit to the link.
 */
type ProfilePageProps =
  | { state: 'ready'; card: PublicProfileCard; device: ProfileDevice; embed?: boolean; countView?: boolean }
  | { state: 'unavailable' };

export default function ProfileLinkPage(props: ProfilePageProps) {
  if (props.state !== 'ready') return <UnavailableProfilePage />;
  return <ReadyProfilePage card={props.card} device={props.device} embed={props.embed === true} countView={props.countView === true} />;
}

function UnavailableProfilePage() {
  const { t } = useTranslation();
  return (
    <>
      <Head>
        <title>{t('profileLink.unavailable.title')}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <ProfileUnavailable />
    </>
  );
}

function ReadyProfilePage({ card, device, embed, countView }: { card: PublicProfileCard; device: ProfileDevice; embed: boolean; countView: boolean }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const og = profileOgMeta(card);
  // The card's OWN name — a business card unfurls as the business, not "<account name> on Symphony".
  const ogTitle = og.name;
  const title = String(t('profileLink.og.title', { name: og.name, productName }));
  const description = og.description ?? String(t('profileLink.og.description', { name: og.name, productName }));
  const { logPageClickBi } = useGenericBi();

  const onCtaTap = useCallback(
    (cta: ProfileCta) => {
      const kind = PROFILE_CTA_TAP[cta];
      logPageClickBi({ ...PROFILE_LINK_PAGE, button_name: kind }, { componentName: `profile-link-${cta}` });
      sendProfileTap(card.handle, card.contextSlug, kind);
    },
    [card.handle, card.contextSlug, logPageClickBi],
  );

  useEffect(() => {
    if (embed) return;
    // The handle IS the owner's referral code (§10): hold it for a web sign-up from this browser. In an
    // effect because storage is client-only and can throw. `allowVanity` is earned by the card itself: a
    // card is only served for an owner inside `symphonyProfileLinks`.
    const record = routeReferralToStore({ code: card.handle, userId: '', now: Date.now(), existing: loadReferral(), allowVanity: true });
    if (record) saveReferral(record);
  }, [card.handle, embed]);

  useEffect(() => {
    // The ONLY thing that counts a view, and it fires from here — after hydration, never from the server
    // render — so an unfurler, a mail scanner or a prefetch is not a view (lib/profile-link-view-beacon.ts).
    // The link is the one the card SHOWS: a context that fell back is counted on the main profile.
    if (embed || !countView) return;
    if (claimProfileView(card.handle, card.contextSlug)) sendProfileView(card.handle, card.contextSlug);
  }, [card.handle, card.contextSlug, countView, embed]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="robots" content={embed ? 'noindex' : og.robots} />
        {/* In someone else's page the iframe's own html/body would paint the app's black body around the card
            (ProfileLanding.module.css, .embed); transparent lets the host page show instead. A page-level rule
            on html/body, which a CSS module cannot hold. */}
        {embed ? <style>{'html,body{background:transparent}'}</style> : null}
        <link rel="canonical" href={og.url} />
        <meta name="description" content={description} />
        {/* The `key`s replace SymphonyApp's generic Symphony card instead of adding a second one. NO
            photo (§5.4): the image is this card's own slide (`?view=og`), drawn from the public card. */}
        <meta key="og:type" property="og:type" content="profile" />
        <meta key="og:title" property="og:title" content={ogTitle} />
        <meta key="og:description" property="og:description" content={description} />
        <meta key="og:url" property="og:url" content={og.url} />
        <meta key="og:image" property="og:image" content={og.image} />
        <meta key="og:image:width" property="og:image:width" content={String(PROFILE_OG_W)} />
        <meta key="og:image:height" property="og:image:height" content={String(PROFILE_OG_H)} />
        <meta key="og:image:alt" property="og:image:alt" content={title} />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" name="twitter:title" content={ogTitle} />
        <meta key="twitter:description" name="twitter:description" content={description} />
        <meta key="twitter:image" name="twitter:image" content={og.image} />
      </Head>
      <ProfileLanding card={card} device={device} embed={embed} onCtaTap={embed ? undefined : onCtaTap} />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<ProfilePageProps> = async ({ params, query, req, res }) => {
  const view = parseProfileView(query?.view);
  if (view === 'embed') {
    res.setHeader('Content-Security-Policy', 'frame-ancestors *');
  } else {
    res.setHeader('Content-Security-Policy', "frame-ancestors 'none'");
    res.setHeader('X-Frame-Options', 'DENY');
  }
  // TODO(profile-links P4b): a short `s-maxage` keyed on (handle, context, revision, locale) with
  // `Vary: Accept-Language` once the card read is real (§5.5). Until then nothing is worth caching, and the
  // CTA order is chosen from the user agent here.
  res.setHeader('Cache-Control', 'private, no-store');

  const bootstrap = getBootstrapContext(req as never);
  const route = parseProfileRoute(params?.handle, params?.context);
  const admitted = route ? admitProfileRequest(req.headers, route.handle) : false;
  const card = route && admitted ? await getPublicProfileCard(route.handle, route.context, { bootstrap, req: req as never }) : null;
  if (!card) {
    if (route && admitted) recordProfileMiss(req.headers);
    res.statusCode = 404;
    return { props: { state: 'unavailable' } };
  }
  if (view === 'slide') {
    await serveSlide(res as never, effectiveCard(card), parseSlideVariant(query?.variant), query?.lang);
    return { props: { state: 'unavailable' } };
  }
  if (view === 'og') {
    const [{ renderProfileOg }, { slideCopy }] = await Promise.all([import('../../../lib/profile-slide'), import('../../../lib/profile-slide-copy')]);
    const { copy, rtl } = slideCopy(query?.lang);
    await servePng(res as never, await renderProfileOg({ card: effectiveCard(card), variant: parseSlideVariant(query?.variant), copy, rtl }));
    return { props: { state: 'unavailable' } };
  }
  if (view === 'qr') {
    await servePng(res as never, await (await import('../../../lib/profile-slide')).renderProfileQr(effectiveCard(card)));
    return { props: { state: 'unavailable' } };
  }
  // `undefined` is not serializable into Next props; a fallback card drops its context fields entirely.
  const served = JSON.parse(JSON.stringify(effectiveCard(card))) as PublicProfileCard;
  const device = profileDeviceFor(req.headers['user-agent']);
  if (view === 'embed') return { props: { state: 'ready', card: served, device, embed: true } };
  return { props: { state: 'ready', card: served, device, countView: !isBotUserAgent(req.headers['user-agent']) } };
};

/**
 * Writes the slide PNG and ends the response, so Next renders nothing after it. The renderer is loaded on
 * demand: sharp and fontkit stay out of the page's ordinary render path.
 */
async function serveSlide(
  res: { statusCode: number; setHeader(name: string, value: string): void; end(body: Buffer): void },
  card: PublicProfileCard,
  variant: ReturnType<typeof parseSlideVariant>,
  lang: unknown,
): Promise<void> {
  const [{ renderProfileSlide }, { slideCopy }] = await Promise.all([
    import('../../../lib/profile-slide'),
    import('../../../lib/profile-slide-copy'),
  ]);
  const { copy, rtl } = slideCopy(lang);
  await servePng(res, await renderProfileSlide({ card, variant, copy, rtl }));
}

/** One PNG answer for the slide and the QR alike. */
async function servePng(res: { statusCode: number; setHeader(name: string, value: string): void; end(body: Buffer): void }, png: Buffer): Promise<void> {
  res.statusCode = 200;
  // The page's own `no-store` is replaced: the image is the PUBLIC card, so a deck refreshed in a loop may
  // reuse it for a few minutes, and a card the owner changes is a new image within that window.
  res.setHeader('Cache-Control', 'public, max-age=300');
  res.setHeader('Content-Type', 'image/png');
  res.setHeader('Content-Length', String(png.length));
  res.end(png);
}
